import { useMemo } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DripDashboardSnapshot } from '../types';
import { DripSection } from './DripSection';

const chartMuted = 'var(--home-muted-on-dark)';
const tickFill = 'rgba(255,255,255,0.45)';
const gridStroke = 'rgba(255,255,255,0.06)';

export function DripAnalyticsCharts({
  series,
  funnel,
  devices,
  topCampaigns,
}: Pick<DripDashboardSnapshot, 'series' | 'funnel' | 'devices' | 'topCampaigns'>) {
  const lineData = useMemo(
    () =>
      series.map((p) => ({
        ...p,
        label: p.date.slice(5),
      })),
    [series],
  );

  const funnelBars = useMemo(
    () => funnel.map((s, i) => ({ ...s, pct: i === 0 ? 100 : Math.round((s.count / funnel[0].count) * 100) })),
    [funnel],
  );

  const pieColors = ['#38bdf8', '#a78bfa', '#f472b6'];

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <DripSection
        id="drip-engagement-series"
        title="Engagement over time"
        description="Opens, clicks, and attributed conversions — trailing window from your ESP feed."
      >
        <div className="h-72 w-full" role="img" aria-label="Line chart of opens, clicks, and conversions by day">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={lineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fill: tickFill, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: tickFill, fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(12,18,32,0.96)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12,
                }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Legend wrapperStyle={{ color: chartMuted, fontSize: 12 }} />
              <Area type="monotone" dataKey="opens" name="Opens" stroke="#38bdf8" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#a78bfa" fill="url(#g2)" strokeWidth={2} />
              <Line type="monotone" dataKey="conversions" name="Conversions" stroke="#f472b6" strokeWidth={2} dot={false} />
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
                </linearGradient>
              </defs>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </DripSection>

      <DripSection
        id="drip-funnel"
        title="Lifecycle funnel"
        description="Sent through converted — cohort scoped to reporting window."
      >
        <div className="h-72 w-full" role="img" aria-label="Funnel from sent to converted">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={funnelBars} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="label"
                width={88}
                tick={{ fill: tickFill, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                contentStyle={{
                  background: 'rgba(12,18,32,0.96)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12,
                }}
              />
              <Bar dataKey="count" name="Count" radius={[0, 8, 8, 0]}>
                {funnelBars.map((_, i) => (
                  <Cell key={funnelBars[i].key} fill={i === 0 ? '#38bdf8' : i === 1 ? '#818cf8' : i === 2 ? '#c084fc' : '#f472b6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </DripSection>

      <DripSection id="drip-devices" title="Device breakdown" description="Unique engagement by device class.">
        <div className="h-64 w-full" role="img" aria-label="Pie chart of device mix">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={devices} dataKey="pct" nameKey="device" innerRadius={52} outerRadius={80} paddingAngle={2}>
                {devices.map((_, i) => (
                  <Cell key={devices[i].device} fill={pieColors[i % pieColors.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'rgba(12,18,32,0.96)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12,
                }}
              />
              <Legend wrapperStyle={{ color: chartMuted, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </DripSection>

      <DripSection id="drip-top-campaigns" title="Top campaigns" description="Composite engagement score — server-defined.">
        <div className="h-64 w-full" role="img" aria-label="Bar chart of top campaigns by score">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topCampaigns} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: tickFill, fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={{ fill: tickFill, fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(12,18,32,0.96)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 12,
                }}
              />
              <Bar dataKey="engagementScore" name="Score" fill="#38bdf8" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </DripSection>
    </div>
  );
}
